import { getLocale, t, useI18n } from '../../i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiCheckCircle,
  FiKey,
  FiMail,
  FiRefreshCw,
  FiShield,
  FiUserPlus,
  FiUsers,
} from 'react-icons/fi';
import {
  adminService,
  type CompanyAdminProfile,
} from '../../services/adminService';
import { sanitizeEmail, sanitizeText } from '../../utils/sanitize';
import './AdminPage.css';

function initialsFor(name: string | null | undefined, email: string | null | undefined): string {
  const source = name?.trim() || email?.trim() || t('Admin');
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
}

function formatDate(value?: string | null): string {
  if (!value) return t('Not recorded');
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t('Not recorded');
  return date.toLocaleDateString(getLocale(), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function validatePassword(password: string): string | null {
  if (password.length < 8) return t('Temporary password must be at least 8 characters');
  if (!/[A-Z]/.test(password)) return t('Temporary password must contain at least one uppercase letter');
  if (!/[a-z]/.test(password)) return t('Temporary password must contain at least one lowercase letter');
  if (!/[0-9]/.test(password)) return t('Temporary password must contain at least one number');
  return null;
}

export default function AdminPage() {
  useI18n();
  const [admins, setAdmins] = useState<CompanyAdminProfile[]>([]);
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(true);
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [adminSuccess, setAdminSuccess] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordConfirm, setAdminPasswordConfirm] = useState('');

  const loadAdmins = useCallback(async () => {
    setIsLoadingAdmins(true);
    setAdminError('');
    try {
      setAdmins(await adminService.fetchCompanyAdmins());
    } catch (err) {
      setAdminError((err as Error).message || t('Failed to load admin accounts'));
    } finally {
      setIsLoadingAdmins(false);
    }
  }, []);

  useEffect(() => {
    void loadAdmins();
  }, [loadAdmins]);

  const summary = useMemo(() => {
    const disabledCount = admins.filter((admin) => admin.status === 'disabled').length;
    return {
      total: admins.length,
      active: admins.length - disabledCount,
      disabled: disabledCount,
    };
  }, [admins]);

  const handleCreateAdmin = async (event: React.FormEvent) => {
    event.preventDefault();

    const cleanName = sanitizeText(adminName, 120);
    const cleanEmail = sanitizeEmail(adminEmail);
    const passwordError = validatePassword(adminPassword);

    if (!cleanName) {
      setAdminError(t('Full name is required'));
      return;
    }
    if (!cleanEmail) {
      setAdminError(t('A valid email address is required'));
      return;
    }
    if (passwordError) {
      setAdminError(passwordError);
      return;
    }
    if (adminPassword !== adminPasswordConfirm) {
      setAdminError(t('Temporary passwords do not match'));
      return;
    }

    setIsCreatingAdmin(true);
    setAdminError('');
    setAdminSuccess('');
    try {
      await adminService.createCompanyAdmin({
        fullName: cleanName,
        email: cleanEmail,
        password: adminPassword,
      });
      setAdminName('');
      setAdminEmail('');
      setAdminPassword('');
      setAdminPasswordConfirm('');
      setAdminSuccess(t('Admin account created for {email}.', { email: cleanEmail }));
      await loadAdmins();
    } catch (err) {
      setAdminError((err as Error).message || t('Failed to create admin account'));
    } finally {
      setIsCreatingAdmin(false);
    }
  };

  return (
    <div className="admin-account-page">
      <div className="admin-account-page__header">
        <div className="admin-account-page__intro">
          <span className="admin-account-page__eyebrow">{t('Admin access')}</span>
          <div className="admin-account-page__title-row">
            <h1 className="admin-account-page__title">{t('Account management')}</h1>
            <span className="admin-account-page__role-pill">
              <FiShield aria-hidden="true" />
              {t('Company admins')}
            </span>
          </div>
          <p className="admin-account-page__subtitle">
            {t('Create and review company administrator accounts for this operations workspace.')}
          </p>
        </div>

        <button
          type="button"
          className="btn btn--secondary admin-account-page__refresh"
          onClick={() => { void loadAdmins(); }}
          disabled={isLoadingAdmins}
        >
          <FiRefreshCw aria-hidden="true" />
          {t('Refresh')}
        </button>
      </div>

      {(adminError || adminSuccess) && (
        <div className="admin-account-page__messages">
          {adminError && <div className="msg--error">{adminError}</div>}
          {adminSuccess && <div className="msg--success">{adminSuccess}</div>}
        </div>
      )}

      <div className="admin-account-page__stats">
        <div className="admin-account-page__stat">
          <span>{t('Total admins')}</span>
          <strong>{summary.total}</strong>
        </div>
        <div className="admin-account-page__stat">
          <span>{t('Active')}</span>
          <strong>{summary.active}</strong>
        </div>
        <div className="admin-account-page__stat admin-account-page__stat--muted">
          <span>{t('Disabled')}</span>
          <strong>{summary.disabled}</strong>
        </div>
      </div>

      <div className="admin-account-page__layout">
        <form className="admin-account-page__panel admin-account-page__form" onSubmit={handleCreateAdmin}>
          <div className="admin-account-page__panel-head">
            <span className="admin-account-page__panel-icon">
              <FiUserPlus aria-hidden="true" />
            </span>
            <div>
              <h2>{t('Create admin account')}</h2>
              <p>{t('New accounts are created in Supabase Auth and linked to this company.')}</p>
            </div>
          </div>

          <label className="form__group">
            <span className="form__label">{t('Full name')}</span>
            <input
              type="text"
              className="form__input"
              value={adminName}
              onChange={(event) => setAdminName(event.target.value)}
              autoComplete="name"
              placeholder={t('Alex Morgan')}
            />
          </label>

          <label className="form__group">
            <span className="form__label">{t('Email')}</span>
            <input
              type="email"
              className="form__input"
              value={adminEmail}
              onChange={(event) => setAdminEmail(event.target.value)}
              autoComplete="email"
              placeholder={t('alex@company.com')}
            />
          </label>

          <div className="admin-account-page__fixed-role">
            <span>{t('Role')}</span>
            <strong>{t('Admin')}</strong>
          </div>

          <label className="form__group">
            <span className="form__label">{t('Temporary password')}</span>
            <input
              type="password"
              className="form__input"
              value={adminPassword}
              onChange={(event) => setAdminPassword(event.target.value)}
              autoComplete="new-password"
            />
          </label>

          <label className="form__group">
            <span className="form__label">{t('Confirm temporary password')}</span>
            <input
              type="password"
              className="form__input"
              value={adminPasswordConfirm}
              onChange={(event) => setAdminPasswordConfirm(event.target.value)}
              autoComplete="new-password"
            />
          </label>

          <button type="submit" className="btn btn--primary admin-account-page__submit" disabled={isCreatingAdmin}>
            <FiKey aria-hidden="true" />
            {isCreatingAdmin ? t('Creating...') : t('Create account')}
          </button>
        </form>

        <section className="admin-account-page__panel admin-account-page__directory">
          <div className="admin-account-page__panel-head">
            <span className="admin-account-page__panel-icon">
              <FiUsers aria-hidden="true" />
            </span>
            <div>
              <h2>{t('Company admins')}</h2>
              <p>{summary.active === 1
                ? t('{count} active account can open this workspace.', { count: summary.active })
                : t('{count} active accounts can open this workspace.', { count: summary.active })}
              </p>
            </div>
          </div>

          {isLoadingAdmins ? (
            <div className="admin-account-page__state">{t('Loading admin accounts...')}</div>
          ) : admins.length === 0 ? (
            <div className="admin-account-page__state">{t('No company admins found.')}</div>
          ) : (
            <div className="admin-account-page__admin-list">
              {admins.map((admin) => {
                const status = admin.status ?? 'active';
                return (
                  <article key={admin.id} className="admin-account-page__admin-row">
                    <span className="admin-account-page__avatar">
                      {initialsFor(admin.full_name, admin.email ?? null)}
                    </span>
                    <div className="admin-account-page__admin-main">
                      <div className="admin-account-page__admin-top">
                        <strong>{admin.full_name || admin.email || t('Unnamed admin')}</strong>
                        <span className={`status-chip ${status === 'active' ? 'status-chip--success' : 'status-chip--neutral'}`}>
                          {t(status === 'active' ? 'Active' : 'Disabled')}
                        </span>
                      </div>
                      <div className="admin-account-page__admin-meta">
                        <span>
                          <FiMail aria-hidden="true" />
                          {admin.email || t('Email unavailable')}
                        </span>
                        <span>
                          <FiCheckCircle aria-hidden="true" />
                          {t('Created {date}', { date: formatDate(admin.created_at) })}
                        </span>
                      </div>
                    </div>
                    <span className="admin-account-page__role-chip">{admin.role === 'admin' ? t('Admin') : admin.role}</span>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
